import axios from 'axios';

// Get configuration from window.config
const config = (window as any).config;

// Create axios instance for backend API calls
const backendAPI = axios.create({
  baseURL: `${config.protocol}://${config.server}:${config.port}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
backendAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Diagnostic APIs
export const diagnosticAPI = {
  // Number Mapping Check
  // phoneNumber should be in +1XXXXXXXXXX format
  getNumberMapping: async (phoneNumber: string) => {
    // Ensure phone number starts with + and is properly formatted
    const formattedPhoneNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    
    // NEW: Direct database call via Flask server
    console.log('🌐 API Call: GET /number-mapping/', formattedPhoneNumber);
    const endpoint = config.backend.endpoints.numberMapping;
    const response = await backendAPI.get(`${endpoint}/${formattedPhoneNumber}`);
    return response;
  },

  // SMS Enablement Check
  getSmsStatus: async (phoneNumber: string) => {
    // Ensure phone number is properly formatted for Bandwidth API
    const formattedPhoneNumber = phoneNumber.startsWith('+1') ? phoneNumber.substring(1) : phoneNumber;
    
    console.log('🌐 API Call: GET /sms-enabled/', formattedPhoneNumber);
    const endpoint = config.backend.endpoints.smsStatus;
    console.log('🌐 API Call: GET /sms-enabled/ endpoint: ', endpoint);
    const response = await backendAPI.get(`${endpoint}/${formattedPhoneNumber}`);
    return response;
  },

  // SMS Processing Status Check
  getSmsProcessingStatus: async (accountId: string, orderId: string, phoneNumber: string) => {
    console.log('🌐 API Call: GET /sms-enabled/', { accountId, orderId, phoneNumber });
    const endpoint = config.backend.endpoints.smsEnabled;
    console.log('🌐 API Call: GET /sms-enabled/ endpoint: ', endpoint, ', phoneNumber: ', phoneNumber);
    const response = await backendAPI.get(`${endpoint}/${accountId}/${orderId}?phoneNumber=${phoneNumber}`);
    return response;
  },

  // Recent Errors Analysis
  getRecentErrors: async (phoneNumber: string, destinationNumber?: string) => {
    const formattedPhoneNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    
    console.log('🌐 API Call: POST /usage for recent error analysis');
    const endpoint = config.backend.endpoints.usage;
    
    // Build the correct payload structure that backend expects
    const payload = {
      timeRange: 'last7Days',
      entities: [
        {
          fromNumbers: [formattedPhoneNumber],
          toNumbers: destinationNumber ? [destinationNumber.startsWith('+') ? destinationNumber : `+${destinationNumber}`] : []
        }
      ]
    };
    
    console.log('📊 Usage API payload:', payload);
    const response = await backendAPI.post(endpoint, payload);
    return response;
  },

  // Campaign Status Check
  getCampaignStatus: async (campaignId: string) => {
    console.log('🌐 API Call: GET /campaign-status/', campaignId);
    const endpoint = config.backend.endpoints.campaignStatus;
    const response = await backendAPI.get(`${endpoint}/${campaignId}`);
    return response;
  },

  // Carrier Approval Status Check
  getCarrierApprovalStatus: async (campaignId: string) => {
    console.log('🌐 API Call: GET /carrier-approval/', campaignId);
    const endpoint = config.backend.endpoints.carrierApproval.replace('{campaign_id}', campaignId);
    const response = await backendAPI.get(endpoint);
    return response;
  },

  // Send Test Message
  sendTestMessage: async (messageData: {
    fromNumber: string;
    toNumber: string;
    message: string;
    groupChannelId?: string; // Now optional since backend constructs it
    platformType: string;
    sentBy: string;
  }) => {
    console.log('🌐 API Call: POST /test-message (Python proxy to Teams backend)');
    
    // Call our Python API endpoint instead of Teams backend directly
    // This keeps the API key secure on the server side
    const payload = {
      fromNumber: messageData.fromNumber,
      toNumber: messageData.toNumber,
      message: messageData.message,
      // groupChannelId: messageData.groupChannelId, // Backend constructs this automatically
      platformType: messageData.platformType,
      sentBy: messageData.sentBy
    };
    
    const endpoint = config.backend.endpoints.testMessage;
    const response = await backendAPI.post(endpoint, payload);
    return response;
  }
}; 
